/**
 * App-Shell — Design 10a (Claude-Design-Brief, Turn 10):
 * - Konto-Menü links unten (Person): Klick auf den Namen öffnet ein Menü nach oben mit
 *   Profil, Benachrichtigungen, Darstellung (Hell/Dunkel/System), Betriebs-Einstellungen,
 *   Hilfe & Support und Abmelden.
 * - Betrieb-Umschalter als eigene Karte darüber (nur bei mehreren Mitgliedschaften klickbar).
 * - Topbar rechts: nur noch die Glocke — das redundante Avatar-„JB" ist entfernt (zeigte
 *   dieselbe Person wie links unten). Theme-Umschaltung lebt jetzt im Konto-Menü; auf Mobile
 *   (Sidebar versteckt) bleibt ein einfacher Hell/Dunkel-Schalter in der Topbar.
 * Dunkle Sidebar (#221D16), Akzent-Orange aktiv. Labels deutsch (deutsches Produkt).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { useApi } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me, Inquiry } from "./types";
import { Icon } from "./components";
import { ROLE_LABEL } from "./format";
import { useTheme, THEME_OPTS } from "./theme";
import { HelpPanel } from "./HelpPanel";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationsBell } from "./NotificationsBell";

const NAV = [
  { to: "/", label: "Übersicht", icon: "dashboard", end: true },
  { to: "/anfragen", label: "Anfragen", icon: "inbox" },
  { to: "/veranstaltungen", label: "Veranstaltungen", icon: "event" },
  { to: "/kalender", label: "Kalender", icon: "calendar_month" },
  { to: "/kunden", label: "Kunden", icon: "group" },
  { to: "/aufgaben", label: "Aufgaben", icon: "checklist" },
  { to: "/katalog", label: "Katalog", icon: "menu_book" },
  { to: "/wissen", label: "Wissen", icon: "help_center" },
  { to: "/zahlungen", label: "Zahlungen", icon: "payments" },
  { to: "/einstellungen", label: "Einstellungen", icon: "settings" },
];

const MOBILE_NAV = NAV.filter((n) =>
  ["/", "/anfragen", "/veranstaltungen", "/kalender", "/einstellungen"].includes(n.to),
);

/** Seitentitel für die Topbar (Design: links steht der aktive Bereichsname, nicht die Wortmarke). */
function useActiveLabel(): string {
  const { pathname } = useLocation();
  if (pathname === "/") return "Übersicht";
  const match = NAV.find((n) => n.to !== "/" && pathname.startsWith(n.to));
  return match?.label ?? "MAESTRO";
}

/** Schließt ein Popup bei Klick außerhalb oder Escape. Ref auf den umschließenden Container legen. */
function useDismiss<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return ref;
}

/** Initialen aus Anzeigename (Vor+Nachname) oder E-Mail. */
function initials(name: string | null | undefined, email: string | null | undefined): string {
  const src = (name ?? "").trim();
  if (src) {
    const parts = src.split(/\s+/);
    const two = (parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "");
    return (two || src[0]!).toUpperCase();
  }
  return (email ?? "?").slice(0, 1).toUpperCase();
}

// Dunkle Sidebar (#221D16): aktiver Eintrag = Akzent-Orange, sonst gedämpftes Cremeweiß.
function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    "flex items-center gap-3 rounded-[9px] px-3 py-2 font-body-sm text-body-sm font-medium transition-colors",
    isActive
      ? "bg-primary text-on-primary font-semibold"
      : "text-[#e2d9ca]/75 hover:bg-white/5 hover:text-inverse-on-surface",
  ].join(" ");
}

/** Ein Eintrag im Konto-Menü. */
function MenuItem({
  icon,
  label,
  hint,
  danger,
  onClick,
}: {
  icon: string;
  label: string;
  hint?: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-body-sm text-body-sm transition-colors",
        danger
          ? "font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
          : "text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/5",
      ].join(" ")}
    >
      <Icon name={icon} className="text-[18px] text-on-surface-variant dark:text-surface-variant" />
      <span className="flex-1 font-medium">{label}</span>
      {hint && <span className="text-[10.5px] text-on-surface-variant dark:text-surface-variant">{hint}</span>}
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const user = useUser();
  const navigate = useNavigate();
  const [theme, setTheme, resolvedDark] = useTheme();
  const activeLabel = useActiveLabel();
  const { data: me } = useApi<Me>("/api/me");
  const { data: inquiries } = useApi<Inquiry[]>("/api/inquiries");
  const openInquiryCount = inquiries?.filter((i) => i.status === "new").length ?? 0;

  const [accountOpen, setAccountOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Merge 2026-07-18: Die globale Command-Palette (main, ⌘K) ersetzt die aeltere
  // A-05-Wissenssuche des Topbar-Felds — eine Suche, ein Verhalten.
  const [searchOpen, setSearchOpen] = useState(false);
  // Globale Tastenkombination ⌘K / Strg+K oeffnet die Suche.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const closeAccount = useCallback(() => setAccountOpen(false), []);
  const closeSwitch = useCallback(() => setSwitchOpen(false), []);
  const accountRef = useDismiss<HTMLDivElement>(accountOpen, closeAccount);
  const switchRef = useDismiss<HTMLDivElement>(switchOpen, closeSwitch);

  // M-27: Membership passend zur aktuellen Subdomain (Multi-Tenant-Nutzer sahen sonst Name/Rolle
  // eines zufaellig ersten Tenants).
  const membership = currentMembership(me);
  const tenantName = membership?.name ?? window.location.hostname.split(".")[0];
  const role = membership?.role;
  const roleLabel = role ? (ROLE_LABEL[role] ?? role) : undefined;
  const memberships = me?.memberships ?? [];
  const canSwitch = memberships.length > 1;

  const personName = user?.displayName?.trim() || user?.primaryEmail || "Konto";
  const personInitials = initials(user?.displayName, user?.primaryEmail);

  // Betrieb wechseln = auf die Subdomain des Ziel-Tenants springen (Host ist die Tenant-Quelle).
  function switchTenant(slug: string) {
    const parts = window.location.hostname.split(".");
    if (parts.length < 2 || parts[0] === slug) return; // lokale Dev-Hosts ohne Subdomain: kein Sprung
    parts[0] = slug;
    const port = window.location.port ? `:${window.location.port}` : "";
    window.location.assign(`${window.location.protocol}//${parts.join(".")}${port}/`);
  }

  return (
    <div className="min-h-screen bg-surface text-[13px] text-on-background dark:bg-night-canvas dark:text-inverse-on-surface">
      {/* SideNavBar (Desktop) — dunkle, warme Sidebar (#221D16) mit Akzent-Orange aktiv */}
      <nav className="fixed left-0 top-0 z-40 hidden h-screen w-sidebar-width flex-col border-r border-white/[0.06] bg-inverse-surface px-3.5 py-[18px] text-inverse-on-surface lg:flex">
        <div className="flex items-center gap-2.5 px-2 pb-5 pt-1">
          <div className="grid h-[26px] w-[26px] place-items-center rounded-lg bg-primary font-serif text-[14px] font-bold leading-none text-on-primary">
            M
          </div>
          <span className="font-label-md text-[13px] font-bold tracking-[0.22em] text-inverse-on-surface">MAESTRO</span>
        </div>
        <div className="mb-4 px-1">
          <button type="button" className="btn-primary w-full" onClick={() => navigate("/anfragen?neu=1")}>
            <Icon name="add" className="text-[18px]" /> Neue Anfrage
          </button>
        </div>
        <ul className="flex-1 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end} className={navLinkClass}>
                <Icon name={item.icon} className="text-[20px]" />
                <span className="flex-1">{item.label}</span>
                {item.to === "/anfragen" && openInquiryCount > 0 && (
                  <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10.5px] font-semibold text-inverse-on-surface">
                    {openInquiryCount}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Konto-Bereich (10a): Betrieb-Karte oben, Person-Karte darunter öffnet das Menü nach oben. */}
        <div className="mt-auto flex flex-col gap-2.5 pt-4">
          {/* Betrieb-Umschalter */}
          <div className="relative" ref={switchRef}>
            {canSwitch && switchOpen && (
              <div className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-background shadow-2xl dark:border-white/10 dark:bg-night-card dark:text-inverse-on-surface">
                <div className="px-3 py-2 font-body-sm text-[10.5px] font-semibold uppercase tracking-wide text-on-surface-variant dark:text-surface-variant">
                  Betrieb wechseln
                </div>
                <div className="max-h-[40vh] overflow-y-auto p-1.5 pt-0">
                  {memberships.map((m) => {
                    const active = m.slug === membership?.slug;
                    return (
                      <button
                        key={m.tenantId}
                        type="button"
                        onClick={() => (active ? closeSwitch() : switchTenant(m.slug))}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5"
                      >
                        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/15 font-serif text-[12px] font-bold text-primary">
                          {(m.name ?? "?").slice(0, 1).toUpperCase()}
                        </div>
                        <span className="flex-1 truncate font-body-sm text-body-sm font-medium">{m.name}</span>
                        {active && <Icon name="check" className="text-[18px] text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              type="button"
              disabled={!canSwitch}
              onClick={() => {
                setAccountOpen(false);
                setSwitchOpen((v) => !v);
              }}
              className={[
                "flex w-full items-center gap-2.5 rounded-[10px] border border-white/[0.12] bg-white/5 px-2.5 py-2 text-left transition-colors",
                canSwitch ? "hover:bg-white/[0.09]" : "cursor-default",
              ].join(" ")}
            >
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-[7px] bg-white/[0.12] text-[11px] font-bold text-inverse-on-surface">
                {(tenantName || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-body-sm text-[12px] font-semibold text-inverse-on-surface">{tenantName}</p>
                <p className="truncate font-body-sm text-[10.5px] text-inverse-on-surface/55">
                  {canSwitch ? "Betrieb wechseln" : (roleLabel ?? "Betrieb")}
                </p>
              </div>
              {canSwitch && <Icon name="unfold_more" className="text-[16px] text-inverse-on-surface/55" />}
            </button>
          </div>

          {/* Person-Karte → Konto-Menü */}
          <div className="relative" ref={accountRef}>
            {accountOpen && (
              <div
                role="menu"
                className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-[72vh] overflow-y-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-background shadow-2xl dark:border-white/10 dark:bg-night-card dark:text-inverse-on-surface"
              >
                {/* Profil-Header */}
                <div className="flex items-center gap-3 border-b border-outline-variant/20 px-3.5 py-3 dark:border-white/10">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 font-serif text-[13px] font-bold text-primary">
                    {personInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-body-sm text-[13.5px] font-semibold">{personName}</span>
                      {roleLabel && (
                        <span className="shrink-0 rounded-full bg-surface-container-high px-2 py-0.5 text-[10px] font-semibold text-on-surface-variant dark:bg-white/10 dark:text-surface-variant">
                          {roleLabel}
                        </span>
                      )}
                    </div>
                    {user?.primaryEmail && (
                      <div className="truncate font-body-sm text-[11.5px] text-on-surface-variant dark:text-surface-variant">
                        {user.primaryEmail}
                      </div>
                    )}
                  </div>
                </div>

                {/* Profil / Benachrichtigungen */}
                <div className="p-1.5">
                  <MenuItem
                    icon="person"
                    label="Mein Profil"
                    onClick={() => {
                      closeAccount();
                      navigate("/mein-konto");
                    }}
                  />
                  <MenuItem
                    icon="notifications"
                    label="Benachrichtigungen"
                    onClick={() => {
                      closeAccount();
                      navigate("/mein-konto?tab=benachrichtigungen");
                    }}
                  />
                </div>

                {/* Darstellung: Hell / Dunkel / System */}
                <div className="flex items-center gap-2 border-t border-outline-variant/20 px-3.5 py-2.5 dark:border-white/10">
                  <span className="flex-1 font-body-sm text-body-sm font-medium">Darstellung</span>
                  <div className="flex gap-0.5 rounded-lg bg-surface-container-high p-0.5 dark:bg-white/5">
                    {THEME_OPTS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        title={opt.label}
                        aria-pressed={theme === opt.value}
                        onClick={() => setTheme(opt.value)}
                        className={[
                          "grid h-[26px] w-8 place-items-center rounded-md transition-colors",
                          theme === opt.value
                            ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                            : "text-on-surface-variant hover:text-on-background dark:text-surface-variant dark:hover:text-inverse-on-surface",
                        ].join(" ")}
                      >
                        <Icon name={opt.icon} className="text-[16px]" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Betriebs-Ebene + Hilfe */}
                <div className="border-t border-outline-variant/20 p-1.5 dark:border-white/10">
                  <MenuItem
                    icon="settings"
                    label="Einstellungen"
                    hint="Betrieb"
                    onClick={() => {
                      closeAccount();
                      navigate("/einstellungen");
                    }}
                  />
                  <MenuItem
                    icon="help"
                    label="Hilfe & Support"
                    onClick={() => {
                      closeAccount();
                      setHelpOpen(true);
                    }}
                  />
                </div>

                {/* Abmelden */}
                <div className="border-t border-outline-variant/20 p-1.5 dark:border-white/10">
                  <MenuItem
                    icon="logout"
                    label="Abmelden"
                    danger
                    onClick={() => {
                      closeAccount();
                      void user?.signOut();
                    }}
                  />
                </div>
              </div>
            )}
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              onClick={() => {
                setSwitchOpen(false);
                setAccountOpen((v) => !v);
              }}
              className={[
                "flex w-full items-center gap-2.5 rounded-[9px] px-2 py-1.5 text-left transition-colors",
                accountOpen ? "bg-white/10 outline outline-1 outline-white/[0.14]" : "hover:bg-white/[0.07]",
              ].join(" ")}
            >
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/[0.16] text-[10.5px] font-bold text-inverse-on-surface">
                {personInitials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-body-sm text-[12px] font-semibold text-inverse-on-surface">{personName}</p>
                <p className="truncate font-body-sm text-[10.5px] text-inverse-on-surface/55">{roleLabel ?? "Konto"}</p>
              </div>
              <Icon
                name="expand_less"
                className={`text-[16px] text-inverse-on-surface/65 transition-transform ${accountOpen ? "" : "rotate-180"}`}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* TopNavBar — Design 10a: Seitentitel links, zentrierte Suche mit ⌘K, rechts NUR die Glocke.
          (Mobile: zusätzlich Hell/Dunkel-Schalter + Abmelden, da die Sidebar dort versteckt ist.) */}
      <header className="sticky top-0 z-30 flex h-topbar-height items-center gap-3 border-b border-[#EAE3D5] bg-[#FFFDF8] px-4 dark:border-white/[0.08] dark:bg-night-bar md:px-margin-desktop lg:ml-sidebar-width">
        {/* Links: mobil Wortmarke (Sidebar versteckt), ab lg der aktive Seitentitel */}
        <div className="flex items-center lg:w-56">
          <span className="font-headline-sm text-headline-sm font-bold text-primary lg:hidden">MAESTRO</span>
          <h1 className="hidden truncate text-[15px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface lg:block">
            {activeLabel}
          </h1>
        </div>

        {/* Mitte: zentrierte Suche mit ⌘K-Chip (Design: max 420px, bg #F6F1E7, radius 9) */}
        <div className="hidden flex-1 justify-center md:flex">
          <div className="group relative w-full max-w-[420px]">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-surface-variant"
            />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="w-full rounded-[9px] border border-transparent bg-[#F6F1E7] py-2 pl-9 pr-14 text-left text-[13px] text-on-surface-variant/70 outline-none transition hover:border-primary/30 focus:border-primary/40 dark:bg-white/5 dark:text-surface-variant/60"
            >
              Suchen …
            </button>
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center rounded-md border border-outline-variant/60 bg-surface-container-lowest px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-on-surface-variant lg:inline-flex dark:border-white/10 dark:bg-white/5 dark:text-surface-variant">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Rechts: Glocke (immer). Auf Mobile zusätzlich Theme-Schalter + Abmelden. */}
        <div className="flex items-center gap-1 md:gap-1.5 lg:w-56 lg:justify-end">
          <button
            type="button"
            className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10 lg:hidden"
            title={resolvedDark ? "Heller Modus" : "Dunkler Modus"}
            onClick={() => setTheme(resolvedDark ? "light" : "dark")}
          >
            <Icon name={resolvedDark ? "light_mode" : "dark_mode"} className="text-xl" />
          </button>
          {/* Merge 2026-07-18: EINE Glocke — der Aktivitäts-Drawer aus main (#18), erweitert
              um die „Braucht Aufmerksamkeit"-Sektion aus dem CX-Audit (jede Meldung klickbar). */}
          <NotificationsBell />
          <button
            type="button"
            className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10 lg:hidden"
            title="Abmelden"
            onClick={() => void user?.signOut()}
          >
            <Icon name="logout" className="text-xl" />
          </button>
        </div>
      </header>

      {/* Main Content Canvas */}
      <main className="mx-auto min-h-[calc(100vh-58px)] max-w-container-max px-4 pb-24 pt-6 md:px-margin-desktop lg:ml-sidebar-width lg:pb-10">
        {children}
      </main>

      {/* Mobile Bottom-Nav (dunkler Karten-Ton, iOS-Safe-Area) */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-outline-variant/30 bg-surface-container-lowest pt-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] dark:border-white/10 dark:bg-night-card lg:hidden">
        {MOBILE_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                "relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1 font-label-md text-[10px]",
                isActive ? "text-primary" : "text-secondary dark:text-secondary-fixed-dim",
              ].join(" ")
            }
          >
            <Icon name={item.icon} className="text-xl" />
            {item.label}
            {item.to === "/anfragen" && openInquiryCount > 0 && (
              <span className="absolute -top-1 right-1 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-primary px-1 text-[8.5px] font-bold text-on-primary">
                {openInquiryCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Hilfe & Support (Design 11d) — Panel von rechts, aus dem Konto-Menü von überall erreichbar */}
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
